'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AircraftType, Customer, AdminSettings } from '@/types';
import { Lock, MoonStar, Plane, UserPlus } from 'lucide-react';
import type { Advisory } from '../../services/advisoryRules';
import { FullContactFormModal } from '../ContactsHub';
import SectionAdvisories from './SectionAdvisories';

type QuoteControlsProps = {
  customerId: string;
  aircraftModel: string;
  date: string;
  pricingMode: 'HOURS' | 'MILEAGE';
  waitingMinutes: number;
  sleepOverEnabled: boolean;
  sleepOverOption: 'NONE' | 'TIER1' | 'TIER2' | 'CUSTOM';
  sleepOverCustomAmount: number;
  customers: Customer[];
  aircrafts: AircraftType[];
  settings: AdminSettings;
  advisories: Advisory[];
  guidanceNotes?: string;
  sleepOverAssessment: {
    autoEligible: boolean;
    reason: string;
  };
  onChange: (updates: Partial<{ customerId: string; aircraftModel: string; date: string; pricingMode: 'HOURS' | 'MILEAGE'; waitingMinutes: number; sleepOverEnabled: boolean; sleepOverOption: 'NONE' | 'TIER1' | 'TIER2' | 'CUSTOM'; sleepOverCustomAmount: number }>) => void;
  onResetDiscountSource: () => void;
  onCustomerCreated?: (c: Customer) => void;
};

const QuoteControls: React.FC<QuoteControlsProps> = ({
  customerId,
  aircraftModel,
  date,
  pricingMode,
  waitingMinutes,
  sleepOverEnabled,
  sleepOverOption,
  sleepOverCustomAmount,
  customers,
  aircrafts,
  settings,
  advisories,
  guidanceNotes = '',
  sleepOverAssessment,
  onChange,
  onResetDiscountSource,
  onCustomerCreated,
}) => {
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement | null>(null);
  const modelNames = aircrafts
    .map(a => String(a.name || '').trim())
    .filter((name): name is string => Boolean(name));
  const modelOptions: string[] = Array.from(new Set<string>(modelNames)).sort((a, b) => a.localeCompare(b));
  const resolvedModel = modelOptions.includes(aircraftModel) ? aircraftModel : modelOptions[0] || '';
  const customer = customers.find(c => c.id === customerId);
  const routeDefaults = settings.routeDefaults || { cruiseSpeedKts: 120, ratePerNmUSD: 0, ratePerHourUSD: 0 };
  const modelCount = resolvedModel ? aircrafts.filter(a => a.name === resolvedModel).length : 0;
  const KTS_TO_MPH = 1.15078;
  // Charter pricing cards must reflect the single registry source of truth.
  const effectiveSpeed = Number(routeDefaults.cruiseSpeedKts || 120);
  const effectiveRatePerHour = Number(routeDefaults.ratePerHourUSD || 0);
  const derivedRatePerMile = effectiveSpeed > 0
    ? effectiveRatePerHour / (effectiveSpeed * KTS_TO_MPH)
    : 0;
  const effectiveRatePerMile = Number(routeDefaults.ratePerNmUSD || 0) > 0
    ? Number(routeDefaults.ratePerNmUSD || 0)
    : derivedRatePerMile;
  const sleepOverTier1 = Number(settings.sleepOverPolicy?.tier1Price ?? 0);
  const sleepOverTier2 = Number(settings.sleepOverPolicy?.tier2Price ?? 0);
  const sleepOverCustomSafe = Math.max(sleepOverTier1, Number(sleepOverCustomAmount || 0));
  const sleepOverUnlocked = sleepOverEnabled || sleepOverAssessment.autoEligible;
  const sleepOverStatusLabel = sleepOverAssessment.autoEligible
    ? 'Late Return'
    : sleepOverEnabled
      ? 'Overnight On'
      : 'Overnight Off';
  const sleepOverHelperText = sleepOverAssessment.autoEligible
    ? sleepOverAssessment.reason
    : sleepOverEnabled
      ? 'Manual overnight enabled. You can price sleep-over as Off, Tier 1, Tier 2, or Custom.'
      : 'Sleep-over stays off unless you enable overnight manually, or the aircraft cannot return to Arusha before 18:30 Tanzania time.';
  const sleepOverStatusClasses = sleepOverAssessment.autoEligible
    ? 'border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 shadow-[0_8px_24px_rgba(245,158,11,0.14)]'
    : sleepOverEnabled
      ? 'border-sky-300 bg-gradient-to-r from-sky-50 to-cyan-50 text-sky-700 shadow-[0_8px_24px_rgba(14,165,233,0.12)]'
      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300';
  const SleepOverStatusIcon = sleepOverAssessment.autoEligible || sleepOverEnabled ? MoonStar : Lock;

  const customerLabel = (c: Customer) => {
    const name = String(c.name || '').trim();
    const email = String(c.email || '').trim();
    return email ? `${name} (${email})` : name;
  };

  useEffect(() => {
    setCustomerQuery(customer ? customerLabel(customer) : '');
  }, [customerId, customer?.name, customer?.email]);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      const root = customerDropdownRef.current;
      if (!root) return;
      if (!root.contains(event.target as Node)) {
        setCustomerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const filteredCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const withRank = customers.map((c, index) => {
      const name = String(c.name || '').toLowerCase();
      const email = String(c.email || '').toLowerCase();
      const company = String(c.company || '').toLowerCase();
      const phone = String(c.phoneNumber || '').toLowerCase();
      const haystack = `${name} ${email} ${company} ${phone}`.trim();
      const starts = q ? (name.startsWith(q) ? 0 : 1) : 0;
      const idx = q ? haystack.indexOf(q) : 0;
      return { c, starts, idx, index };
    });
    return withRank
      .filter(item => !q || item.idx >= 0)
      .sort((a, b) => {
        if (a.starts !== b.starts) return a.starts - b.starts;
        if (a.idx !== b.idx) return a.idx - b.idx;
        return a.index - b.index;
      })
      .slice(0, 12)
      .map(item => item.c);
  }, [customers, customerQuery]);

  const selectCustomer = (next: Customer) => {
    onChange({ customerId: next.id });
    onResetDiscountSource();
    setCustomerQuery(customerLabel(next));
    setCustomerDropdownOpen(false);
  };

  return (
    <div className="space-y-7">
      {/* Pricing Mode Toggle */}
      <div className="rounded-2xl border border-slate-200/70 bg-white shadow-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <span className="text-[10px] font-bold text-slate-900 uppercase tracking-[0.15em]">Pricing Method</span>
          <span className="text-[10px] font-medium text-indigo-600 uppercase tracking-wider">Registry Defaults</span>
        </div>
        
        <div className="flex items-center gap-1.5 p-1.5 rounded-xl bg-slate-50 border border-slate-200">
          {(['HOURS', 'MILEAGE'] as const).map(mode => (
            <button
              key={mode}
              type="button"
              onClick={() => onChange({ pricingMode: mode })}
              className={`flex-1 px-4 py-3 rounded-lg text-[10px] font-bold uppercase tracking-[0.15em] transition-all ${
                pricingMode === mode
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {mode === 'HOURS' ? 'Time (Hours)' : 'Mileage (Distance)'}
            </button>
          ))}
        </div>

        {/* Rate Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Speed</span>
            <p className="text-xl font-bold text-slate-900 mt-1">{effectiveSpeed || 0} <span className="text-sm font-normal text-slate-400">kts</span></p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Rate / Mile</span>
            <p className="text-xl font-bold text-slate-900 mt-1">${effectiveRatePerMile || 0}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Rate / Hour</span>
            <p className="text-xl font-bold text-slate-900 mt-1">${effectiveRatePerHour || 0}</p>
          </div>
        </div>
        <div className="mt-5">
          <SectionAdvisories title="Pricing Guard" section="PRICING" advisories={advisories} compact />
        </div>
      </div>

      {/* Client & Aircraft */}
      <div className="rounded-2xl border border-slate-200/70 bg-white shadow-sm p-6">
        <span className="text-[10px] font-bold text-slate-900 uppercase tracking-[0.15em]">Client & Aircraft</span>
        
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2.4fr)_minmax(0,1.4fr)_minmax(0,1fr)] gap-5 mt-5">
          {/* Customer Select */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Client Identity</span>
              <button
                type="button"
                onClick={() => setShowQuickCreate(true)}
                className="flex items-center gap-1 text-[10px] text-indigo-600 hover:text-indigo-700 font-medium"
              >
                <UserPlus className="h-3 w-3" />New
              </button>
            </div>
            <div className="relative" ref={customerDropdownRef}>
              <input
                type="text"
                placeholder="Type client name, email, company, phone..."
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                value={customerQuery}
                onFocus={() => setCustomerDropdownOpen(true)}
                onChange={e => {
                  setCustomerQuery(e.target.value);
                  setCustomerDropdownOpen(true);
                }}
                onBlur={() => {
                  if (!customer) return;
                  if (!customerQuery.trim()) setCustomerQuery(customerLabel(customer));
                }}
              />
              {customerDropdownOpen && filteredCustomers.length > 0 && (
                <div className="absolute z-30 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-xl max-h-72 overflow-auto">
                  {filteredCustomers.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectCustomer(c)}
                      className={`w-full px-4 py-3 text-left hover:bg-indigo-50/60 transition-colors border-b last:border-b-0 border-slate-100 ${
                        c.id === customerId ? 'bg-indigo-50' : 'bg-white'
                      }`}
                    >
                      <p className="text-sm font-semibold text-slate-900 break-words">{c.name}</p>
                      <p className="text-xs text-slate-500 break-words">
                        {[c.email, c.company, c.phoneNumber].filter(Boolean).join(' · ') || 'No extra details'}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-1">
              <span className="text-[10px] font-medium text-indigo-600 uppercase tracking-wider">Selected</span>
              <p className="text-base font-semibold text-slate-900 mt-0.5 whitespace-normal break-words">{customer?.name}</p>
            </div>
          </div>

          {showQuickCreate && (
            <FullContactFormModal
              onSaved={c => {
                onCustomerCreated?.(c);
                onChange({ customerId: c.id });
                onResetDiscountSource();
                setShowQuickCreate(false);
              }}
              onClose={() => setShowQuickCreate(false)}
            />
          )}

          {/* Aircraft Model Select */}
          <div className="space-y-2">
            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Airframe Allocation</span>
            <select
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 appearance-none cursor-pointer"
              value={resolvedModel}
              title={resolvedModel}
              onChange={e => onChange({ aircraftModel: e.target.value })}
            >
              {modelOptions.map(model => (
                <option key={model} value={model} title={model}>
                  {model}
                </option>
              ))}
            </select>
            <div className="mt-1">
              <span className="text-[10px] font-medium text-indigo-600 uppercase tracking-wider">Selected</span>
              <p className="text-base font-semibold text-slate-900 mt-0.5 whitespace-normal break-words">
                {resolvedModel || 'Unassigned'}
              </p>
              {resolvedModel && (
                <p className="text-sm text-slate-600">
                  {modelCount} active tail{modelCount === 1 ? '' : 's'}
                </p>
              )}
            </div>
          </div>

          {/* Date Picker */}
          <div className="space-y-2">
            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Issue Date</span>
            <input
              type="date"
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 outline-none transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              value={date}
              onChange={e => onChange({ date: e.target.value })}
            />
          </div>
        </div>
        <div className="mt-5">
          <SectionAdvisories title="Mission Guard" section="MISSION" advisories={advisories} notes={guidanceNotes} compact />
        </div>
      </div>

      {/* Waiting Time */}
      <div className="rounded-2xl border border-slate-200/70 bg-white shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-slate-900 uppercase tracking-[0.15em]">Waiting Time</span>
            <div className="flex items-center gap-4 mt-1">
              <input
                type="number"
                min="0"
                className="w-24 bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-900 text-center outline-none transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                value={waitingMinutes}
                onChange={e => onChange({ waitingMinutes: parseInt(e.target.value || '0', 10) })}
              />
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Sleep Over</p>
                <button
                  type="button"
                  onClick={() => onChange({
                    sleepOverEnabled: !sleepOverEnabled,
                    sleepOverOption: !sleepOverEnabled
                      ? (sleepOverOption === 'NONE' ? 'TIER1' : sleepOverOption)
                      : 'NONE',
                  })}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-all ${sleepOverStatusClasses}`}
                >
                  <SleepOverStatusIcon className="h-3.5 w-3.5" />
                  {sleepOverStatusLabel}
                </button>
              </div>
              <p className="text-xs leading-5 text-slate-500">{sleepOverHelperText}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onChange({ sleepOverOption: 'NONE' })}
                  disabled={!sleepOverUnlocked}
                  className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${sleepOverOption === 'NONE' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'} ${!sleepOverUnlocked ? 'cursor-not-allowed opacity-50 hover:border-slate-200' : ''}`}
                >
                  Off
                </button>
                <button
                  type="button"
                  onClick={() => onChange({ sleepOverOption: 'TIER1' })}
                  disabled={!sleepOverUnlocked}
                  className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${sleepOverOption === 'TIER1' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'} ${!sleepOverUnlocked ? 'cursor-not-allowed opacity-50 hover:border-slate-200' : ''}`}
                >
                  Tier 1 (${sleepOverTier1})
                </button>
                <button
                  type="button"
                  onClick={() => onChange({ sleepOverOption: 'TIER2' })}
                  disabled={!sleepOverUnlocked}
                  className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${sleepOverOption === 'TIER2' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'} ${!sleepOverUnlocked ? 'cursor-not-allowed opacity-50 hover:border-slate-200' : ''}`}
                >
                  Tier 2 (${sleepOverTier2})
                </button>
                <button
                  type="button"
                  onClick={() => onChange({ sleepOverOption: sleepOverOption === 'CUSTOM' ? 'NONE' : 'CUSTOM' })}
                  disabled={!sleepOverUnlocked}
                  className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${sleepOverOption === 'CUSTOM' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'} ${!sleepOverUnlocked ? 'cursor-not-allowed opacity-50 hover:border-slate-200' : ''}`}
                >
                  Custom
                </button>
              </div>
              {sleepOverUnlocked && sleepOverOption === 'CUSTOM' && (
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={sleepOverTier1}
                    className="w-32 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 outline-none transition-all focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    value={sleepOverCustomAmount}
                    onChange={e => {
                      const parsed = parseFloat(e.target.value || '0');
                      onChange({ sleepOverCustomAmount: Number.isFinite(parsed) ? Math.max(sleepOverTier1, parsed) : sleepOverTier1 });
                    }}
                  />
                  <span className="text-xs text-slate-500">Min: ${sleepOverTier1}</span>
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Rates</span>
            <p className="text-xs text-slate-600 mt-0.5">Free: {settings.waitFreeMinutes || 0} min</p>
            <p className="text-xs text-slate-600">Rate: ${settings.waitRatePerHour || 0}/hr</p>
            <p className="text-xs text-slate-600">Sleep Over: {!sleepOverUnlocked || sleepOverOption === 'NONE' ? '$0' : sleepOverOption === 'TIER1' ? `$${sleepOverTier1}` : sleepOverOption === 'TIER2' ? `$${sleepOverTier2}` : `$${sleepOverCustomSafe}`}</p>
          </div>
        </div>
        <div className="mt-5">
          <SectionAdvisories title="Timing Guard" section="TIMING" advisories={advisories} compact />
        </div>
      </div>
    </div>
  );
};

export default QuoteControls;
